/**
 * AdminModal — V2 modal shell for admin surfaces.
 *
 * Behavior:
 *   - Backdrop dim + click-outside-to-close
 *   - Escape closes
 *   - Focus is moved into the dialog on open; the previously focused element is
 *     restored on close
 *   - Body scroll is locked while open
 *   - On mobile (<640px) the dialog becomes a bottom sheet (full width, docked
 *     to bottom edge) so form fields have room; on ≥640px it centers as a card
 *   - Optional `size` prop switches between `sm` / `md` / `lg` / `xl`
 *
 * Props:
 *   - open        : boolean (required — parent controls mount/unmount)
 *   - onClose     : () => void
 *   - title       : string
 *   - description : optional short subtitle string
 *   - size        : "sm" | "md" | "lg" | "xl"   (default "md")
 *   - testId      : root data-testid (default "admin-modal")
 *   - children    : dialog body
 *   - footer      : optional Save/Cancel row rendered in a sticky footer
 */
import { useEffect, useRef } from "react";
import { X } from "lucide-react";

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
};

export default function AdminModal({
  open,
  onClose,
  title,
  description,
  size = "md",
  testId = "admin-modal",
  children,
  footer,
}) {
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Focus management + body scroll lock + Escape-to-close.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the dialog root (or first focusable inside).
    const t = setTimeout(() => {
      const first =
        dialogRef.current?.querySelector(
          'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
        ) || dialogRef.current;
      first?.focus();
    }, 20);

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the trigger.
      if (previouslyFocused.current && previouslyFocused.current.focus) {
        try { previouslyFocused.current.focus(); } catch { /* noop */ }
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4"
      style={{ background: "rgba(15,15,20,0.55)" }}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        aria-describedby={description ? `${testId}-desc` : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${SIZES[size]} rounded-t-2xl sm:rounded-2xl border shadow-2xl max-h-[92vh] flex flex-col outline-none`}
        style={{
          background: "var(--v2-surface)",
          borderColor: "var(--v2-border)",
          color: "var(--v2-text)",
        }}
      >
        <header
          className="flex items-start justify-between gap-4 px-5 py-4 border-b"
          style={{ borderColor: "var(--v2-border)" }}
        >
          <div className="min-w-0">
            <h2
              id={`${testId}-title`}
              data-testid={`${testId}-title`}
              className="text-base font-semibold tracking-tight truncate"
            >
              {title}
            </h2>
            {description ? (
              <p
                id={`${testId}-desc`}
                className="mt-0.5 text-xs"
                style={{ color: "var(--v2-text-muted)" }}
              >
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testId}-close`}
            aria-label="Close"
            className="rounded-md p-1 flex-shrink-0 transition-colors"
            style={{ color: "var(--v2-text-muted)" }}
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="overflow-y-auto flex-1 px-5 py-4">{children}</div>
        {footer ? (
          <footer
            className="flex flex-wrap items-center justify-end gap-2 px-5 py-3 border-t"
            style={{ borderColor: "var(--v2-border)", background: "var(--v2-surface-subtle)" }}
            data-testid={`${testId}-footer`}
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
